import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CPMemberService } from 'src/app/cp-member/services/CPMember.service';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/shared/services/alert.service';
import { timeout } from 'rxjs/operators';

@Component({
  selector: 'enforce-password-change',
  templateUrl: './enforce-password-change.component.html',
  styleUrls: ['./enforce-password-change.component.scss'],
})
export class EnforcePasswordChangeComponent implements OnInit, OnDestroy {
  title = 'Set New Password';
  saveButtonText =  'Submit';

  disableSaveButton = true;
  error = null;
  form: FormGroup;
  oldPassError = '';
  newPassError = '';
  confirmPassError = '';
  isPasswordChangedSuccessfully = false;

  private subs = new Subscription();

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private cpMemberService: CPMemberService,
    private fb: FormBuilder,
    private alertService: AlertService
  ) {
    // Build form with validators that match your template expectations.
    this.form = this.fb.group(
      {
        oldPass: ['', [Validators.required]],
        newPass: [
          '',
          [
            Validators.required,
            Validators.minLength(14),
            // Strong password pattern: at least one lowercase, uppercase, digit, and special char.
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{14,}$/),
          ],
        ],
        confirmPass: ['', [Validators.required]],
      },
      {
        // Use group-level validators (plural) and set mismatch on confirm control.
        validators: [this.confirmMatchValidator()],
      }
    );

    // Keep the Save button disabled while the form is invalid/pending.
    this.disableSaveButton = this.form.invalid || this.form.pending;
    this.subs.add(
      this.form.statusChanges.subscribe(() => {
        this.disableSaveButton = this.form.invalid || this.form.pending;
        this.updateConfirmPassErrorMessage();
      })
    );

    // If you want dynamic inline messages, you can also subscribe to valueChanges:
    // this.subs.add(this.form.valueChanges.subscribe(() => this.updateErrorMessages()));
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Optional: granular rules validator (currently NOT attached to newPass;
   * you can attach it if you want messages per rule instead of a generic pattern error).
   */
  passwordRulesValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    const errors: any = {};

    if (value.length < 14) {
      errors.minlength = { requiredLength: 14, actualLength: value.length };
    }
    if (!/[A-Z]/.test(value)) {
      errors.uppercase = true;
    }
    if (!/[a-z]/.test(value)) {
      errors.lowercase = true;
    }
    if (!/[0-9]/.test(value)) {
      errors.number = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors.specialChar = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  /**
   * Validator that keeps 'mismatch' as an error on confirmPass control
   * so your template's *ngIf on confirmPass.hasError('mismatch') works.
   */
  private confirmMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const newPass = group.get('newPass');
      const confirmPass = group.get('confirmPass');

      if (!newPass || !confirmPass) return null;

      const np = newPass.value;
      const cp = confirmPass.value;
      const mismatch = !!np && !!cp && np !== cp;

      const currentErrors = confirmPass.errors || {};

      if (mismatch) {
        // Add/keep the mismatch error on confirmPass
        if (!currentErrors['mismatch']) {
          confirmPass.setErrors({ ...currentErrors, mismatch: true });
        }
      } else {
        // Remove only the mismatch error, keep any others (like 'required')
        if (confirmPass.hasError('mismatch')) {
          const { mismatch, ...rest } = currentErrors;
          confirmPass.setErrors(Object.keys(rest).length ? rest : null);
        }
      }

      return null; // Do not set a group-level error; we set it on the control.
    };
  }

  /** Keep messages in sync (used if you prefer programmatic messages). */
  private updateErrorMessages(): void {
    // Old password
    const oldPass = this.form.get('oldPass');
    this.oldPassError = '';
    if (oldPass && (oldPass.touched || oldPass.dirty)) {
      if (oldPass.hasError('required')) {
        this.oldPassError = 'Old Password is required.';
      }
    }

    // New password
    const newPass = this.form.get('newPass');
    this.newPassError = '';
    if (newPass && (newPass.touched || newPass.dirty)) {
      const errs = newPass.errors || {};
      if (errs['required']) {
        this.newPassError = 'New Password is required.';
      } else if (errs['minlength']) {
        const actual = errs['minlength'].actualLength || 0;
        this.newPassError = `Minimum 14 characters required (${actual}/14).`;
      } else if (errs['pattern']) {
        this.newPassError = 'Please follow the pattern.';
      } else if (errs['uppercase']) {
        this.newPassError = 'At least one uppercase letter is required.';
      } else if (errs['lowercase']) {
        this.newPassError = 'At least one lowercase letter is required.';
      } else if (errs['number']) {
        this.newPassError = 'At least one number is required.';
      } else if (errs['specialChar']) {
        this.newPassError = 'At least one special character is required (e.g., @, #, $).';
      }
    }

    // Confirm password (extra line to support your {{ confirmPassError }} binding)
    this.updateConfirmPassErrorMessage();
  }

  private updateConfirmPassErrorMessage(): void {
    const confirmPass = this.form.get('confirmPass');
    this.confirmPassError = '';

    if (!confirmPass) return;

    if (confirmPass.touched || confirmPass.dirty) {
      if (confirmPass.hasError('required')) {
        this.confirmPassError = 'Please confirm the new password.';
      } else if (confirmPass.hasError('mismatch')) {
        this.confirmPassError = 'Passwords do not match.';
      }
    } else {
      // If form already marked mismatch and user has typed something
      if (confirmPass.value && confirmPass.hasError('mismatch')) {
        this.confirmPassError = 'Passwords do not match.';
      }
    }
  }

  close(): void {
    this.isPasswordChangedSuccessfully ? this.signout() : '';
    if (!this.isPasswordChangedSuccessfully) {
      this.form.markAllAsTouched();
      this.updateErrorMessages();

      if (this.form.invalid) {
        return;
      }

      const { oldPass, newPass } = this.form.value as { oldPass: string; newPass: string };

      const payload = {
        UserName: '',
        OldPassword: oldPass,
        NewPassword: newPass,
      };

      // Take one value from userName observable, use it to call changePassword, then dismiss modal.

      const userNameSub = this.authService.userName.subscribe({
        next: name => {
          payload.UserName = name || '';
          this.cpMemberService
            .changePassword(payload)
            .then(() => {
              // this.modalService.dismissAll(EnforcePasswordChangeComponent);
              // Optionally: this.signout(); // if policy requires re-login after change

              this.isPasswordChangedSuccessfully = true;
              this.saveButtonText = "OK";
            })
            .catch(err => {
              // TODO: handle error (toast/snackbar/log)
              console.error('Password change failed:', err);
              this.error = err;
              this.alertService.error('Password change failed:', err);
            })
            .finally(() => {
              userNameSub.unsubscribe();
            });
        },
        error: () => {
          userNameSub.unsubscribe();
        },
      });
    }
  }

  signout(): void {
    this.authService.signout();
  }
}
